export const TIER3_ARRIVAL_DWELL_MS = 10_000;
export const TIER3_ARRIVAL_RADIUS_METRES = 35;
export const TIER3_STATIONARY_SPEED_MPS = 0.8;

const toRadians = (degrees) => (Number(degrees) * Math.PI) / 180;

export function distanceMetres(a, b) {
  if (
    a?.latitude == null || a?.longitude == null ||
    b?.latitude == null || b?.longitude == null
  ) return null;

  const lat1 = toRadians(a.latitude);
  const lat2 = toRadians(b.latitude);
  const dLat = lat2 - lat1;
  const dLon = toRadians(Number(b.longitude) - Number(a.longitude));
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;
  return 6_371_000 * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
}

export function nextArrivalDwell(
  previous,
  { device, destination, nowMs, radiusMetres = TIER3_ARRIVAL_RADIUS_METRES,
    dwellMs = TIER3_ARRIVAL_DWELL_MS,
    stationarySpeedMps = TIER3_STATIONARY_SPEED_MPS }
) {
  const distance = distanceMetres(device, destination);
  // Missing GPS speed is unknown, not proof that the vehicle is stationary.
  const speed = device?.speed;
  const hasUsableSpeed = Number.isFinite(speed) && speed >= 0;
  const inside = distance != null && distance <= radiusMetres;
  const stationary = hasUsableSpeed && speed <= stationarySpeedMps;

  if (!inside || !stationary) {
    return { sinceMs: null, ready: false, distanceMetres: distance, inside, stationary };
  }

  const sinceMs = previous?.sinceMs ?? nowMs;
  return {
    sinceMs,
    ready: nowMs - sinceMs >= dwellMs,
    distanceMetres: distance,
    inside,
    stationary,
  };
}


// Part 3 arrival also works when iOS omits GPS speed while stopped. In that
// case, require multiple fresh, accurate fixes inside the saved destination
// whose positions remain in a small cluster for the full dwell. This only
// reveals the parked confirmation screen; it never advances the stage itself.
export function nextPart3ArrivalDwell(previous, {
  device, destination, nowMs, radiusMetres = 100, dwellMs = 10_000,
  stationarySpeedMps = 0.8, stationaryRadiusMetres = 18,
} = {}) {
  const distance = distanceMetres(device, destination);
  const accuracy = device?.accuracy_metres;
  const accurate = Number.isFinite(accuracy) && accuracy >= 0 && accuracy <= 100;
  // Use the same saved-stop radius as the server. Adding GPS uncertainty to
  // the distance made close arrivals impossible on typical iPhone fixes.
  const inside = distance != null && accurate && distance <= radiusMetres;
  const speed = device?.speed;
  const hasUsableSpeed = Number.isFinite(speed) && speed >= 0;
  const previousAt = previous?.lastFixAt;
  const anchor = previous?.positionAnchor;
  const consecutive = inside && previous?.inside === true && Number.isFinite(previousAt)
    && nowMs > previousAt && nowMs - previousAt <= 15_000;
  const stableCluster = consecutive && anchor
    && distanceMetres(device, anchor) <= stationaryRadiusMetres;
  const speedStationary = hasUsableSpeed && speed <= stationarySpeedMps;
  const stationary = hasUsableSpeed ? speedStationary : Boolean(stableCluster);
  const continuousStationary = hasUsableSpeed
    ? speedStationary && previous?.stationary === true && consecutive
    : Boolean(stableCluster);
  const candidateSinceMs = continuousStationary
    ? (previous.candidateSinceMs ?? previous.sinceMs ?? previousAt)
    : nowMs;
  const sampleCount = continuousStationary ? (previous.sampleCount || 1) + 1 : 1;
  const sinceMs = stationary
    ? (continuousStationary ? previous.sinceMs : candidateSinceMs)
    : null;

  return {
    sinceMs,
    candidateSinceMs,
    positionAnchor: stableCluster ? anchor : { latitude: device?.latitude, longitude: device?.longitude },
    lastFixAt: nowMs,
    sampleCount,
    ready: stationary && sampleCount >= 3 && sinceMs != null && nowMs - sinceMs >= dwellMs,
    distanceMetres: distance,
    inside,
    stationary,
  };
}
